import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  FileCheck,
  Package,
  Users,
  CalendarCheck,
  Home,
  DollarSign,
} from "lucide-react";
import type { ProjectReadiness, ReadinessItem, ReadinessItemStatus } from "@/data/portal/intelligence";

const STATUS_CONFIG: Record<ReadinessItemStatus, { icon: typeof CheckCircle2; chipClass: string; label: string }> = {
  ready: { icon: CheckCircle2, chipClass: "bg-success/10 text-success dark:text-success", label: "Ready" },
  pending: { icon: Clock, chipClass: "bg-warning/10 text-warning dark:text-warning", label: "Pending" },
  at_risk: { icon: AlertTriangle, chipClass: "bg-warning/10 text-warning dark:text-warning", label: "At Risk" },
  blocked: { icon: XCircle, chipClass: "bg-destructive/10 text-destructive dark:text-destructive", label: "Blocked" },
};

const ITEM_ICONS: Record<string, typeof FileCheck> = {
  permits: FileCheck,
  materials: Package,
  crew: Users,
  schedule: CalendarCheck,
  site: Home,
  financial: DollarSign,
};

interface Props {
  readiness: ProjectReadiness;
}

export function ProjectReadinessPanel({ readiness }: Props) {
  const { items, summary, overallStatus } = readiness;

  const summaryColor = overallStatus === "ready"
    ? "text-success dark:text-success"
    : overallStatus === "blocked"
      ? "text-destructive dark:text-destructive"
      : "text-warning dark:text-warning";

  return (
    <Card className="border-border/40">
      <CardContent className="pt-5 pb-4 space-y-4">
        {/* Summary line */}
        <p className={`text-sm font-medium ${summaryColor}`}>
          {summary}
        </p>

        {/* Readiness grid */}
        <TooltipProvider delayDuration={200}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {items.map(item => (
              <ReadinessCell key={item.key} item={item} />
            ))}
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}

function ReadinessCell({ item }: { item: ReadinessItem }) {
  const config = STATUS_CONFIG[item.status];
  const StatusIcon = config.icon;
  const ItemIcon = ITEM_ICONS[item.key] || FileCheck;

  const cell = (
    <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
      <div className="mt-0.5 shrink-0">
        <ItemIcon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-[11px] font-semibold tracking-wide uppercase text-muted-foreground leading-none">
          {item.label}
        </p>
        <div className="flex items-center gap-1.5">
          <StatusIcon className={`h-3 w-3 shrink-0 ${config.chipClass.split(" ").filter(c => c.startsWith("text-")).join(" ")}`} />
          <span className="text-xs text-foreground truncate">{item.displayText}</span>
        </div>
      </div>
    </div>
  );

  if (!item.description) return cell;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{cell}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[220px] text-xs">
        {item.description}
      </TooltipContent>
    </Tooltip>
  );
}
